import {
  afterNextRender,
  ChangeDetectionStrategy,
  DestroyRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { AudioPlayerTone } from './audio.model';
import { formatPlaybackTime, resamplePeaks } from './audio-waveform.utils';

const KEYBOARD_SEEK_STEP_SECONDS = 5;
const BAR_WIDTH_PX = 3;
const BAR_GAP_PX = 2;

@Component({
  selector: 'fc-audio-waveform',
  imports: [NgClass],
  template: `
    <div class="grid h-full grid-cols-1 items-center">
      <div
        class="col-start-1 row-start-1 flex h-full min-w-0 items-center gap-2"
        aria-hidden="true"
      >
        @for (peak of displayedPeaks(); track $index) {
          <span
            class="w-3 min-w-1 rounded-full"
            [ngClass]="unplayedBarClass()"
            [style.height.%]="peak * 100"
          ></span>
        }
      </div>
      <div
        class="col-start-1 row-start-1 flex h-full min-w-0 items-center gap-2"
        [style.clip-path]="playedClipPath()"
        aria-hidden="true"
      >
        @for (peak of displayedPeaks(); track $index) {
          <span
            class="w-3 min-w-1 rounded-full bg-primary-600"
            [style.height.%]="peak * 100"
          ></span>
        }
      </div>
    </div>
  `,
  host: {
    class:
      'block rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary-700',
    '[class.h-24]': 'compact()',
    '[class.h-28]': '!compact()',
    '[class.cursor-pointer]': 'seekable()',
    '[class.touch-pan-y]': 'seekable()',
    '[attr.role]': 'seekable() ? "slider" : null',
    '[attr.tabindex]': 'seekable() ? 0 : null',
    '[attr.aria-label]': 'seekable() ? ariaLabel() : null',
    '[attr.aria-valuemin]': 'seekable() ? 0 : null',
    '[attr.aria-valuemax]': 'seekable() ? roundedDuration() : null',
    '[attr.aria-valuenow]': 'seekable() ? roundedCurrentTime() : null',
    '[attr.aria-valuetext]': 'seekable() ? ariaValueText() : null',
    '[attr.aria-hidden]': 'seekable() ? null : "true"',
    '(pointerdown)': 'startPointerSeek($event)',
    '(pointermove)': 'continuePointerSeek($event)',
    '(pointerup)': 'endPointerSeek($event)',
    '(pointercancel)': 'endPointerSeek($event)',
    '(keydown)': 'seekWithKeyboard($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioWaveformComponent {
  peaks = input.required<number[]>();
  progress = input<number>(0);
  durationSeconds = input<number | null>(null);
  tone = input<AudioPlayerTone>('neutral');
  seekable = input<boolean>(false);
  compact = input<boolean>(false);
  fitBarsToWidth = input<boolean>(false);
  ariaLabel = input<string>('Seek audio');

  seekRequested = output<number>();

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly isDragging = signal(false);
  private readonly availableWidth = signal<number | null>(null);

  constructor() {
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      if (!this.fitBarsToWidth()) return;
      const resizeObserver = new ResizeObserver(([entry]) => {
        if (entry) this.availableWidth.set(entry.contentRect.width);
      });
      resizeObserver.observe(this.elementRef.nativeElement);
      destroyRef.onDestroy(() => resizeObserver.disconnect());
    });
  }

  protected readonly unplayedBarClass = computed(() =>
    this.tone() === 'own' ? 'bg-primary-300' : 'bg-secondary-400',
  );

  protected readonly displayedPeaks = computed(() => {
    const peaks = this.peaks();
    if (!this.fitBarsToWidth()) return peaks;
    const availableWidth = this.availableWidth();
    if (availableWidth === null) return [];
    const barCount = Math.max(
      1,
      Math.floor((availableWidth + BAR_GAP_PX) / (BAR_WIDTH_PX + BAR_GAP_PX)),
    );
    return resamplePeaks(peaks, barCount);
  });

  protected readonly playedClipPath = computed(() => {
    const unplayedPercent = (1 - clampRatio(this.progress())) * 100;
    return `inset(0 ${unplayedPercent}% 0 0)`;
  });

  protected readonly roundedDuration = computed(() =>
    Math.round(this.durationSeconds() ?? 0),
  );

  protected readonly roundedCurrentTime = computed(() =>
    Math.round(clampRatio(this.progress()) * (this.durationSeconds() ?? 0)),
  );

  protected readonly ariaValueText = computed(
    () =>
      `${formatPlaybackTime(this.roundedCurrentTime())} of ${formatPlaybackTime(this.durationSeconds())}`,
  );

  protected startPointerSeek(event: PointerEvent): void {
    if (!this.seekable() || event.button !== 0) return;
    this.elementRef.nativeElement.setPointerCapture(event.pointerId);
    this.isDragging.set(true);
    this.emitSeekAtPointer(event);
  }

  protected continuePointerSeek(event: PointerEvent): void {
    if (this.isDragging()) this.emitSeekAtPointer(event);
  }

  protected endPointerSeek(event: PointerEvent): void {
    if (!this.isDragging()) return;
    this.isDragging.set(false);
    this.elementRef.nativeElement.releasePointerCapture(event.pointerId);
  }

  protected seekWithKeyboard(event: KeyboardEvent): void {
    if (!this.seekable()) return;
    const duration = this.durationSeconds() ?? 0;
    const stepRatio =
      duration > 0 ? KEYBOARD_SEEK_STEP_SECONDS / duration : 0.1;
    const currentRatio = clampRatio(this.progress());

    const targetRatio = {
      ArrowLeft: currentRatio - stepRatio,
      ArrowDown: currentRatio - stepRatio,
      ArrowRight: currentRatio + stepRatio,
      ArrowUp: currentRatio + stepRatio,
      Home: 0,
      End: 1,
    }[event.key];
    if (targetRatio === undefined) return;

    event.preventDefault();
    this.seekRequested.emit(clampRatio(targetRatio));
  }

  private emitSeekAtPointer(event: PointerEvent): void {
    const bounds = this.elementRef.nativeElement.getBoundingClientRect();
    if (bounds.width === 0) return;
    this.seekRequested.emit(
      clampRatio((event.clientX - bounds.left) / bounds.width),
    );
  }
}

function clampRatio(ratio: number): number {
  return Math.min(1, Math.max(0, ratio));
}
